<template>
  <div id="collection-results-container">
    <BackComponent />

    <!-- Show loader if axios request has not completed yet -->
    <LoaderComponent v-if="!loaded" />
    <!-- Once axios request is successful, display page content -->
    <div id="wrapper" v-else>
      <!-- Results information -->
      <div id="info-container">
        <div id="info-text">
          <!-- Display current applied filters -->
          <p>
            Collection | <span> {{ currentFilters.string }} </span>
          </p>
          <!-- Display number of results found out of total available -->
          <p>{{ pageInfo.total }} result(s) out of {{ total }} objects</p>
        </div>
      </div>

      <div
        id="top-page-navigation"
        class="page-navigation-wrapper"
        v-if="displayPageNavigation"
      >
        <PagesComponent
          :pageInfo="pageInfo"
          :navigatePage="navigatePage"
          :navigatePageDirect="navigatePageDirect"
        ></PagesComponent>
      </div>

      <!-- Main content (Results and filters) -->
      <div id="content-container">
        <!-- Results of search -->
        <!-- Show only if results exist, if not, a "No results" message is displayed instead -->
        <div id="collection-results" v-if="resultsExist">
          <ObjectGrid
            :results="results"
            :lessThan974="lessThan974"
            :hover="hover"
            :show="show"
          ></ObjectGrid>
          <!-- Run v-for on every result object -->
          <!-- Add event listeners for object pop-up on hover -->
          <!-- lessThan974 checks window width to conditionally apply mouse events -->
          <!-- <div class="entry-container"
          v-for="(result, index) in results" :key="result.imagePath"
          :class="`entry-${index + 1}`" 
          v-on="lessThan974 ? {} : 
            { mouseover: () => hoverHandler('over', index), 
            mouseleave: () => hoverHandler('leave', index) }">
          <div class="entry">
            <div class="entry-image-wrapper">
              <img class="entry-image" :src="result.imagePath" />
            </div> -->
          <!-- On hover, show the appropriate pop-up -->
          <!-- Always show "pop-up" if lessThan974 is true (smaller screens) -->
          <!-- <div class="entry-details" v-show="(!lessThan974 && hover && show === index) || lessThan974"> -->
          <!-- Hide image link if lessThan974 is true (smaller screens) -->
          <!-- <router-link class="entry-details-img-wrapper" v-if="!lessThan974" :to="{ 
                name: 'database-object', 
                params: { project: result.projectId, country: result.countryId, id: result.partnerId, object: result.objectId, language: result.languageId }}">
                <img :src="result.imagePath" />
              </router-link>
              <div class="entry-text-container">
                <p class="entry-name" v-html="result.name"></p>
                <p class="entry-date" v-html="result.objectDate"></p>
                <p class="entry-partner">{{ result.partnerName }}</p>
                <p class="entry-location">{{ result.location }}, {{ result.countryName }}</p>
                <p class="entry-project">for <span>{{ result.projectName }}</span></p>
                <router-link :to="{ 
                  name: 'database-object', 
                  params: { project: result.projectId, country: result.countryId, id: result.partnerId, object: result.objectId, language: result.languageId }}">
                  <p class="entry-link">See Database Entry &gt;</p>
                </router-link>
              </div>
            </div>
          </div>
        </div> -->
        </div>
        <!-- If no results, show message instead -->
        <div id="no-results" v-else>
          <div>
            No results. Click <button @click="resetFilters()">here</button> to
            reset all filters.
          </div>
        </div>

        <div id="options-container">
          <div id="refine-icon" v-if="lessThan499" @click="showFilters = true">
            <font-awesome-icon :icon="['fas', 'pen-to-square']" />
          </div>
          <div id="refine" v-show="!lessThan499 || showFilters">
            <div
              id="refine-close"
              v-if="showFilters && lessThan499"
              @click="showFilters = false"
            >
              <font-awesome-icon :icon="['fas', 'xmark']" />
            </div>
            <!-- Change label depending if there are already filters applied -->
            <div id="filter-label-wrapper">
              <div class="options-label" v-if="isFirstSearch">Filter by:</div>
              <div class="options-label" v-else>Filter further by:</div>
            </div>
            <!-- All select fields execute filtering upon selection -->
            <!-- Country options -->
            <div id="select-wrapper">
              <select
                id="country-option"
                class="search-option"
                v-model="country"
                @change="filterResults('country', country)"
                :disabled="!resultsExist"
              >
                <option selected disabled value="">Select Country</option>
                <option
                  v-for="country in countries"
                  :key="country[0]"
                  :value="country[0]"
                >
                  {{ country[1] }}
                </option>
              </select>
              <!-- 
              Type, Dynasty, Subject, and Material fields will be hidden
              if there are no applicable fields to filter by
            -->
              <!-- Type options -->
              <select
                id="type-option"
                class="search-option"
                v-if="typesAvailable"
                v-model="type"
                @change="filterResults('type', type)"
                :disabled="!resultsExist"
              >
                <option selected disabled value="">Type</option>
                <option
                  v-for="type in types"
                  :key="type[0]"
                  :value="type[0]"
                  v-html="type[1]"
                ></option>
              </select>
              <!-- Dynasty options -->
              <select
                id="dynasty-option"
                class="search-option"
                v-if="dynastiesAvailable"
                v-model="dynasty"
                @change="filterResults('dynasty', dynasty)"
                :disabled="!resultsExist"
              >
                <option selected disabled value="">Period / Dynasty</option>
                <option
                  v-for="dynasty in dynasties"
                  :key="dynasty[0]"
                  :value="dynasty[0]"
                  v-html="dynasty[1]"
                ></option>
              </select>
              <!-- Subject options -->
              <select
                id="subject-option"
                class="search-option"
                v-if="subjectsAvailable"
                v-model="subject"
                @change="filterResults('subject', subject)"
                :disabled="!resultsExist"
              >
                <option selected disabled value="">Subject</option>
                <option
                  v-for="subject in subjects"
                  :key="subject[0]"
                  :value="subject[0]"
                  v-html="subject[1]"
                ></option>
              </select>
              <!-- Material options -->
              <select
                id="material-option"
                class="search-option"
                v-if="materialsAvailable"
                v-model="material"
                @change="filterResults('material', material)"
                :disabled="!resultsExist"
              >
                <option selected disabled value="">Material</option>
                <option
                  v-for="material in materials"
                  :key="material[0]"
                  :value="material[0]"
                  v-html="material[1]"
                ></option>
              </select>
              <div id="date-wrapper">
                <!-- Start date options -->
                <select
                  id="start-date-option"
                  class="search-option"
                  v-model="start"
                  @change="filterResults('start', start)"
                  :disabled="!resultsExist"
                >
                  <option selected disabled value="">Start Date</option>
                  <option v-for="date in years" :key="date[0]" :value="date[0]">
                    {{ date[1] }}
                  </option>
                </select>
                <!-- End date options -->
                <select
                  id="end-date-option"
                  class="search-option"
                  v-model="end"
                  @change="filterResults('end', end)"
                  :disabled="!resultsExist"
                >
                  <option selected disabled value="">End Date</option>
                  <option v-for="date in years" :key="date[0]" :value="date[0]">
                    {{ date[1] }}
                  </option>
                </select>
              </div>
            </div>
            <!-- Reset button -->
            <div id="reset-container">
              <button id="reset" @click="resetFilters()">Reset filters</button>
            </div>
          </div>

          <div id="timeline" v-if="showTimelineLink">
            <div class="options-label">Timeline for this Search</div>
            <div id="timeline-link">
              <!-- ID for icon needed for targeted styling -->
              <font-awesome-icon id="timeline-icon" icon="fa-solid fa-share" />
              <router-link
                :to="{
                  name: 'timeline-results',
                  query: { c: country, start: startParam, end: endParam },
                }"
              >
                Timeline | {{ countryDisplay }}
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <div
        id="bottom-page-navigation"
        class="page-navigation-wrapper"
        v-if="displayPageNavigation"
      >
        <PagesComponent
          :pageInfo="pageInfo"
          :navigatePage="navigatePage"
          :navigatePageDirect="navigatePageDirect"
        ></PagesComponent>
      </div>
      <!-- Extra space when there are no results -->
      <div id="bottom-padding" v-else></div>
    </div>
  </div>
</template>



<script>
import BackComponent from "./BackComponent.vue";
import LoaderComponent from "./LoaderComponent.vue";
import PagesComponent from "./PagesComponent.vue";
import ObjectGrid from "./ObjectGrid.vue";
import media from "../mixins/media.js";
// Contains functions for handling data for select fields
import collection from "../mixins/collection.js";
import pageDisplay from "../mixins/pageDisplay.js";
import characters from "../mixins/characters.js";
export default {
  name: "CollectionResults",
  components: {
    BackComponent: BackComponent,
    LoaderComponent: LoaderComponent,
    PagesComponent: PagesComponent,
    ObjectGrid: ObjectGrid,
  },
  mixins: [media, collection, pageDisplay, characters],
  data() {
    return {
      lessThan974: window.matchMedia("(max-width: 974px)").matches,
      lessThan499: window.matchMedia("(max-width: 499px)").matches,
      showFilters: false,
      loaded: false,
      resultsExist: true,
      displayPageNavigation: false,
      isFirstSearch: false,
      // Total number of object results
      total: "",
      // Path, current/last page, total pages, etc.
      pageInfo: {},
      // Links to first/last/next/previous pages
      pages: {},
      results: [],
      countries: {},
      types: {},
      dynasties: {},
      subjects: {},
      materials: {},
      years: {},
      typesAvailable: true,
      dynastiesAvailable: true,
      subjectsAvailable: true,
      materialsAvailable: true,
      country: "",
      type: "",
      dynasty: "",
      subject: "",
      material: "",
      start: "",
      end: "",
      // Hide/show object popup
      hover: false,
      // Track which object popup to hide/show
      show: -1,
      // URL for API call
      apiFilter: "",
      // Track/save current filters applied to search
      currentFilters: {
        queries: [],
        selections: [],
        string: "",
      },
      // List of country codes with Timeline information
      timelineCountries: {},
      showTimelineLink: false,
      countryDisplay: "",
      startParam: "",
      endParam: "",
      urls: {
        portal: process.env.VUE_APP_PORTAL_URL,
        baroqueArt: process.env.VUE_APP_BAROQUE_ART_URL,
        islamicArt: process.env.VUE_APP_ISLAMIC_ART_URL,
        sharingHistory: process.env.VUE_APP_SHARING_HISTORY_URL,
        galleries: process.env.VUE_APP_GALLERIES_URL,
        images: process.env.VUE_APP_IMAGES_URL,
      },
      emails: {
        contactUs: process.env.VUE_APP_CONTACT_US_EMAIL,
      },
    };
  },
  watch: {
    // Activity on route runs main function to get and display object results
    $route() {
      if (this.$route.name === "collection-results") {
        this.showFilters = false;
        this.getObjects();
      }
    },
  },
  methods: {
    // Main function for getting and displaying results
    getObjects: function () {
      // Build the URL for API call from applied filters,
      // which are "saved" as queries in current URL
      let baseURL = `${process.env.VUE_APP_DXA_ENDPOINT}/items?`;
      let addURL = [];
      let fullURL;
      let query = Object.entries(this.$route.query);
      // Reset selections
      this.resetFields();
      // Push corresponding URL fragment into array
      for (let i = 0; i < query.length; i++) {
        if (query[i][0] === "country") {
          addURL.push(`ic[]=${query[i][1]}`);
          this.country = query[i][1];
        } else if (query[i][0] === "type") {
          addURL.push(`id[]=${query[i][1]}`);
          this.type = query[i][1];
        } else if (query[i][0] === "dynasty") {
          addURL.push(`id[]=${query[i][1]}`);
          this.dynasty = query[i][1];
        } else if (query[i][0] === "subject") {
          addURL.push(`id[]=${query[i][1]}`);
          this.subject = query[i][1];
        } else if (query[i][0] === "material") {
          addURL.push(`id[]=${query[i][1]}`);
          this.material = query[i][1];
        } else if (query[i][0] === "start") {
          addURL.push(`na=${query[i][1]}`);
          this.start = query[i][1];
        } else if (query[i][0] === "end") {
          addURL.push(`nz=${query[i][1]}`);
          this.end = query[i][1];
        } else if (query[i][0] === "page") {
          addURL.push(`page=${query[i][1]}`);
        }
        // Save current filters
        this.currentFilters.queries.push(query[i]);
      }
      // Join URL fragments into string separated by &
      addURL = addURL.join("&");
      this.apiFilter = "&".concat(addURL);
      fullURL = baseURL.concat(addURL);
      return axios({ method: "GET", url: fullURL }).then(
        (result) => {
          // Sort by startDate key to order items chronologically
          let sorted = Object.values(result.data.items).sort(function (a, b) {
            return a.startDate - b.startDate;
          });
          let dateTruncated = sorted;
          for (let key in dateTruncated) {
            let date = dateTruncated[key].objectDate;
            if (this.charactersOver80(date)) {
              dateTruncated[key].objectDate = this.cutOff(date);
            }
          }
          this.results = dateTruncated;
          this.pageInfo = result.data.page;
          this.pages = result.data.links;
          this.setIsFirstSearch();
          Promise.all([
            this.loadCountries(this.apiFilter),
            this.getCountriesWithEvents(),
            this.loadTags(this.apiFilter),
            this.loadYears(this.apiFilter),
          ]).then(() => {
            this.setTimelineInfo();
            this.setShowTimelineLink();
            this.getCurrentFilters();
            this.setAvailableFields();
          });
          this.setResultsExist();
          this.setDisplayPageNavigation();
          this.loaded = true;
        },
        (error) => {
          this.$router.replace({ name: "error" });
          console.error(error);
        }
      );
    },
    // Reset data
    resetFields: function () {
      this.country = "";
      this.type = "";
      this.dynasty = "";
      this.subject = "";
      this.material = "";
      this.start = "";
      this.end = "";
      this.currentFilters = {
        queries: [],
        selections: [],
        string: "",
      };
      this.showTimelineLink = false;
      this.countryDisplay = "";
      this.startParam = "";
      this.endParam = "";
    },

    // Check if there are results, reset fields
    setResultsExist: function () {
      if (Object.entries(this.results).length === 0) {
        this.resetFields();
        this.resultsExist = false;
      } else {
        this.resultsExist = true;
      }
    },

    // Get total number of objects in project
    getTotalNumberObjects: function () {
      return axios({
        method: "GET",
        url: `${process.env.VUE_APP_DXA_ENDPOINT}/objects/count?hash=${process.env.VUE_APP_API_HASH}`,
      }).then(
        (result) => {
          this.total = result.data.total;
        },
        (error) => {
          console.error(error);
        }
      );
    },

    // Change display text for "filter by" (first search)
    // vs "filter further by" (not first search)
    setIsFirstSearch: function () {
      let query = Object.entries(this.$route.query).length;
      if (query === 0) {
        this.isFirstSearch = true;
      } else {
        this.isFirstSearch = false;
      }
    },

    // Create list of current filters for display
    getCurrentFilters: function () {
      // Clear filters (empty array)
      this.currentFilters.selections = [];
      let queries = this.currentFilters.queries;
      // let selections = this.currentFilters.selections;
      // Use ID of each filter type applied
      // to get text of selected filter
      // Ex. "Austria", "Bottle"
      for (let i = 0; i < queries.length; i++) {
        if (queries[i][0] === "country") {
          this.pushSelected("country-option");
        } else if (queries[i][0] === "type") {
          this.pushSelected("type-option");
        } else if (queries[i][0] === "dynasty") {
          this.pushSelected("dynasty-option");
        } else if (queries[i][0] === "subject") {
          this.pushSelected("subject-option");
        } else if (queries[i][0] === "material") {
          this.pushSelected("material-option");
        } else if (queries[i][0] === "start") {
          this.pushSelected("start-date-option");
        } else if (queries[i][0] === "end") {
          this.pushSelected("end-date-option");
        }
      }
      // Join strings together for display
      if (this.currentFilters.selections.length > 0) {
        this.currentFilters.string = this.currentFilters.selections.join(" & ");
      } else if (Object.entries(this.results).length === 0) {
        this.currentFilters.string = "No Results";
      } else {
        this.currentFilters.string = "All Objects";
      }
    },
    // Get text of selected filter
    pushSelected: function (id) {
      let sel = document.getElementById(id);
      let text;
      if (id === "start-date-option") {
        text = "(Start) " + sel.options[sel.selectedIndex].text;
      } else if (id === "end-date-option") {
        text = "(End) " + sel.options[sel.selectedIndex].text;
      } else {
        text = sel.options[sel.selectedIndex].text;
      }
      this.currentFilters.selections.push(text);
    },

    // Disable selection field if there are no available filters
    setAvailableFields: function () {
      // Check if objects exist
      // resultsExist is not updated yet
      let objsExist = Object.entries(this.results).length !== 0;
      if (this.types.length === 0 && objsExist) {
        this.typesAvailable = false;
      } else {
        this.typesAvailable = true;
      }
      if (this.dynasties.length === 0 && objsExist) {
        this.dynastiesAvailable = false;
      } else {
        this.dynastiesAvailable = true;
      }
      if (this.subjects.length === 0 && objsExist) {
        this.subjectsAvailable = false;
      } else {
        this.subjectsAvailable = true;
      }
      if (this.materials.length === 0 && objsExist) {
        this.materialsAvailable = false;
      } else {
        this.materialsAvailable = true;
      }
    },

    // Trigger reload when selecting additional filter
    filterResults: function (type, selection) {
      let currentQuery = JSON.parse(JSON.stringify(this.$route.query));
      let updateQuery = currentQuery;
      updateQuery[`${type}`] = selection;
      // Start from first page again
      delete updateQuery["page"];
      this.$router.push({ name: "collection-results", query: updateQuery });
    },

    // [Reset button] triggers reload with no queries (returns all objects)
    resetFilters: function () {
      this.$router.push({ name: "collection-results", query: "" });
    },

    setTimelineInfo: function () {
      // Look for matching country code to display name in Timeline link
      let search = function (search, object) {
        for (let i = 0; i < object.length; i++) {
          if (object[i][0] == search) {
            return object[i][1];
          }
        }
      };
      this.countryDisplay = search(this.country, this.countries);
      // Get year range for overall Timeline
      return axios({
        method: "GET",
        url: `${process.env.VUE_APP_DXA_ENDPOINT}/events/years?hash=${process.env.VUE_APP_API_HASH}`,
      }).then(
        (result) => {
          this.startParam = result.data.minYear;
          this.endParam = result.data.maxYear;
        },
        (error) => {
          console.error(error);
        }
      );
    },
    // Get list of countries that have Timeline information
    getCountriesWithEvents: function () {
      return axios({
        method: "GET",
        url: `${process.env.VUE_APP_DXA_ENDPOINT}/events/countries?hash=${process.env.VUE_APP_API_HASH}`,
      }).then(
        (result) => {
          // Format of data returned:
          // {countryId: "dz", countryName: "Algeria"}
          let array = [];
          for (let object in result.data.items) {
            array.push(Object.values(result.data.items[object]));
          }
          let codes = [];
          for (let i = 0; i < array.length; i++) {
            codes.push(array[i][0]);
          }
          // Save array of country codes
          this.timelineCountries = codes;
        },
        (error) => {
          console.error(error);
        }
      );
    },
    // If country selected has Timeline information, show link
    setShowTimelineLink: function () {
      if (this.country === "") {
        this.showTimelineLink = false;
      } else {
        // Check against list in timelineCountries
        // this.countries = selected country code
        if (this.timelineCountries.indexOf(this.country) !== -1) {
          this.showTimelineLink = true;
        } else {
          this.showTimelineLink = false;
        }
      }
    },

    navigatePage: function (page) {
      let url;
      if (page === "first") {
        url = this.pages[0].href;
      } else if (page === "last") {
        url = this.pages[1].href;
      } else if (page === "next") {
        url = this.pages[2].href;
      } else if (page === "previous") {
        url = this.pages[3].href;
      }
      return axios({ method: "GET", url: url }).then(
        (result) => {
          this.results = result.data.items;
          this.pageInfo = result.data.page;
          this.pages = result.data.links;
          // JSON.parse required to update query
          // Otherwise, router doesn't detect a change
          let currentQuery = JSON.parse(JSON.stringify(this.$route.query));
          let updateQuery = currentQuery;
          updateQuery["page"] = this.pageInfo.currentPage;
          this.$router.push({ name: "collection-results", query: updateQuery });
        },
        (error) => {
          console.error(error);
        }
      );
    },
    // Navigate directly to a page by clicking a number
    navigatePageDirect: function (page) {
      let currentQuery = JSON.parse(JSON.stringify(this.$route.query));
      let updateQuery = currentQuery;
      updateQuery["page"] = page;
      this.$router.push({ name: "collection-results", query: updateQuery });
    },
  },
  created() {
    this.getObjects();
    this.getTotalNumberObjects();
  },
};
</script>



<style scoped lang="scss">
#collection-results-container {
  @include flex-center;
  flex-direction: column;
  width: 100%;
  background-color: white;
}
#collection-results-container .fas {
  font-size: 75%;
  padding-right: 4px;
}
#collection-results-container #timeline-icon {
  padding-right: 0;
}

#back {
  @include flex-center;
  justify-content: flex-start;
  width: 100%;
  padding: 30px 50px 0 50px;
}
#back button {
  @include button-styles;
  padding: 2px 8px 2px 0;
  font-weight: bold;
}

.loader-container {
  min-height: 400px;
}

#wrapper {
  width: 100%;
}

#info-container {
  @include flex-center;
  justify-content: space-between;
  width: 100%;
}
#info-text {
  @include flex-center;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 50px 0 50px;
}
#info-text span {
  font-weight: bold;
  // Preserve whitespace
  white-space: pre;
}

#content-container {
  @include flex-center;
  align-items: flex-start;
  width: 100%;
}

#collection-results {
  width: 78%;
  padding: 30px 0 30px 50px;
}
#no-results {
  width: 78%;
  padding: 30px 0 30px 50px;
}
#no-results button {
  @include button-styles;
  color: $link-blue;
  text-decoration: underline;
}

#options-container {
  @include flex-center;
  flex-direction: column;
  width: 22%;
}

#select-wrapper {
  width: 100%;
}

#refine {
  @include flex-center;
  flex-direction: column;
  width: 100%;
  padding: 30px 50px 30px 25px;
}
#filter-label-wrapper {
  width: 100%;
}
.options-label {
  width: 100%;
  max-width: 225px;
  padding-bottom: 10px;
  font-weight: bold;
}
.search-option {
  width: 100%;
  max-width: 225px;
  font-size: 90%;
  padding: 4px;
  margin-top: 6px;
}
#reset-container {
  width: 100%;
  max-width: 225px;
  font-size: 90%;
  padding-top: 10px;
  margin-top: 6px;
}
#reset {
  @include button-styles;
  border: 1px solid $theme-light;
  border-radius: 50px;
  background-color: $theme-light;
  color: $light-text;
  padding: 6px 10px;
}

#timeline {
  @include flex-center;
  flex-direction: column;
  width: 100%;
  padding: 30px 50px 30px 25px;
}
#timeline-link {
  width: 100%;
  max-width: 225px;
}
#timeline-link a {
  text-decoration: none;
  color: black;
}
#timeline-link a:hover {
  @include link-hover-styles;
}

#bottom-padding {
  margin-bottom: 75px;
}

/* IE-only */
@media only screen and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  #collection-results {
    @include IE-grid-styles;
  }
}

@media only screen and (max-width: 1199px) {
  #collection-results {
    width: 72%;
  }
  #no-results {
    width: 72%;
  }
  #options-container {
    width: 28%;
  }
}

@media only screen and (max-width: 974px) {
  #content-container {
    flex-direction: column-reverse;
  }
  #collection-results {
    width: 100%;
    padding-right: 50px;
  }
  #no-results {
    width: 100%;
    padding-right: 50px;
  }
  #options-container {
    width: 100%;
  }
  #refine {
    padding: 30px 50px;
  }
  #select-wrapper {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 5px;
  }
  .search-option {
    max-width: 100%;
  }
  #date-wrapper {
    @include flex-center;
  }
  #start-date-option {
    margin-right: 2.5px;
  }
  #end-date-option {
    margin-left: 2.5px;
  }
  #reset-container {
    width: 100%;
    max-width: 100%;
  }
  #timeline {
    align-items: flex-start;
    padding: 0 50px;
  }
}

/* IE-only */
@media only screen and (-ms-high-contrast: none) and (max-width: 974px),
  (-ms-high-contrast: active) and (max-width: 974px) {
  #select-wrapper {
    display: flex;
    flex-wrap: wrap;
  }
  .search-option,
  #date-wrapper {
    width: calc(33.3% - 5px);
    margin-right: 5px;
  }
  #date-wrapper {
    margin-right: 0;
  }
  #date-wrapper .search-option {
    width: 50%;
    margin-right: 0;
  }
  #end-date-option {
    margin-left: 5px;
  }
}

@media only screen and (max-width: 849px) {
  #select-wrapper {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* IE-only */
@media only screen and (-ms-high-contrast: none) and (max-width: 849px),
  (-ms-high-contrast: active) and (max-width: 849px) {
  #select-wrapper {
    display: flex;
    flex-wrap: wrap;
  }
  .search-option,
  #date-wrapper {
    width: calc(50% - 5px);
  }
}

@media only screen and (max-width: 599px) {
  #back {
    padding: 30px 30px 0 30px;
  }
  #info-text {
    padding: 10px 30px 0 30px;
  }
  #timeline {
    padding: 0 30px;
  }
  #collection-results {
    padding: 30px;
  }
  #no-results {
    padding: 30px;
  }
  #refine {
    padding: 30px;
  }
}

@media only screen and (max-width: 499px) {
  #collection-results-container {
    justify-content: flex-start;
    position: relative;
    min-height: 350px;
  }
  #refine-icon {
    @include flex-center;
    position: absolute;
    top: 0;
    right: 0;
    height: 40px;
    width: 40px;
    font-size: 26px;
    border-radius: 50px;
    color: $light-text;
    background-color: $theme-light;
    margin: 15px 15px 0 0;
  }
  #refine {
    position: absolute;
    top: 0;
    left: 0;
    background-color: $theme-light;
    z-index: 52;
  }
  #refine-close {
    @include flex-center;
    position: absolute;
    top: 0;
    right: 0;
    height: 30px;
    width: 30px;
    font-size: 30px;
    color: $light-text;
    margin: 5px;
  }
  #filter-label-wrapper .options-label {
    color: $light-text;
  }
  #select-wrapper {
    @include flex-center;
    flex-direction: column;
  }
  .search-option {
    width: 100%;
  }
  #date-wrapper {
    width: 100%;
  }
  #reset {
    border: none;
    background-color: $theme-medium-dark;
  }
  #timeline {
    margin-top: 20px;
  }
}

/* IE-only */
@media only screen and (-ms-high-contrast: none) and (max-width: 499px),
  (-ms-high-contrast: active) and (max-width: 499px) {
  .search-option {
    margin-right: 0;
    margin-left: 0;
  }
}
</style>